import React, { Component } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { AlertTriangle } from 'react-feather';

/**
 * ErrorBoundary component to catch errors in React component tree
 * and display a user-friendly fallback UI
 */
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Catch errors in any components below and re-render with error message
        this.setState({
            error: error,
            errorInfo: errorInfo
        });

        // You can also log the error to an error reporting service
        console.error("Error caught by ErrorBoundary:", error, errorInfo);
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    }

    render() {
        if (this.state.hasError) {
            // You can render any custom fallback UI
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 4,
                        height: '100%',
                        width: '100%'
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            textAlign: 'center',
                            borderRadius: '10px',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            maxWidth: 500
                        }}
                    >
                        <AlertTriangle
                            size={48}
                            color="#F59E0B"
                            style={{
                                marginBottom: 16
                            }}
                        />
                        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                            Something went wrong
                        </Typography>
                        <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                            We're having trouble displaying this component.
                            You can try refreshing the page or contact support if the problem persists.
                        </Typography>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <Box
                                sx={{
                                    textAlign: 'left',
                                    backgroundColor: '#f7f7f7',
                                    p: 2,
                                    borderRadius: '4px',
                                    mb: 3,
                                    maxHeight: '200px',
                                    overflow: 'auto',
                                    fontSize: '0.875rem',
                                    fontFamily: 'monospace'
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Error Details (visible in development only):
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <div>{this.state.error && this.state.error.toString()}</div>
                                    <div style={{ marginTop: 8 }}>
                                        {this.state.errorInfo && this.state.errorInfo.componentStack}
                                    </div>
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={this.handleReset}
                                sx={{ borderRadius: '8px' }}
                            >
                                Try Again
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => window.location.reload()}
                                sx={{ borderRadius: '8px' }}
                            >
                                Refresh Page
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            );
        }

        // Return children if there's no error
        return this.props.children;
    }
}

export default ErrorBoundary;